import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Icon,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Text,
  useDisclosure,
  SimpleGrid,
  VStack,
  Box,
  useToast,
} from "@chakra-ui/react";
import {
  FaTrash,
  FaCalendarAlt,
  FaListUl,
  FaUserTie,
  FaPlus,
  FaTags,
} from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const Sub_Cat_form = () => {
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [eventId, setEventId] = useState(""); // Using eventId instead of eventTitle
  const [events, setEvents] = useState([]); // Store fetched events
  const [categories, setCategories] = useState([]); // Store fetched categories
  const [filteredCategories, setFilteredCategories] = useState([]); // Store filtered categories
  const [teachers, setTeachers] = useState([]); // Store fetched teachers
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch events from API
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const schoolId = sessionStorage.getItem("school_id"); // Replace with actual school_id
        const response = await axios.get(
          `${BASE_URL}/admin/events/event/list_all_event.php?school_id=${schoolId}`
        );
        console.log("Events:", response);

        if (response.data.success) {
          setEvents(response.data.data.events);
        } else {
          toast({
            title: "Error Fetching Events",
            description: `Error fetching events: ${response.data.message}`,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        toast({
          title: "Error Fetching Events",
          description: "An error occurred while fetching events.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    fetchEvents();
  }, []);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const schoolId = sessionStorage.getItem("school_id");
        const response = await axios.get(
          `${BASE_URL}/admin/events/category/list_category.php?school_id=${schoolId}`
        );
        console.log("Categories:", response);

        if (response.data.success) {
          setCategories(response.data.data); // Store categories in state
        } else {
          toast({
            title: "Error Fetching Categories",
            description: `Error fetching categories: ${response.data.message}`,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast({
          title: "Error Fetching Categories",
          description: "An error occurred while fetching categories.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    fetchCategories();
  }, []);

  // Filter categories based on selected event_id
  useEffect(() => {
    if (eventId) {
      // If categories are available, filter them by event_id
      const filtered = categories.filter((category) => {
        console.log("Category event_id:", category.event_id);
        return category.event_id == eventId;
      });

      // If no categories are found, show a fallback message (including eventId)
      if (filtered.length === 0) {
        console.log(`No categories found for event with ID: ${eventId}`);
      }

      // Update the filtered categories in state
      setFilteredCategories(filtered);
    } else {
      // Reset filtered categories if no event is selected
      setFilteredCategories([]);
    }
  }, [eventId, categories]);

  // Fetch teachers from API
  const fetchTeachers = async () => {
    try {
      const jwt = sessionStorage.getItem("jwt"); // If JWT is needed
      const school_id = sessionStorage.getItem("school_id"); // If JWT is needed

      const response = await axios.get(
        `${BASE_URL}/admin/staff/staff_list.php?school_id=${school_id}`,
        {
          headers: {
            Authorization: `Bearer ${jwt}`,
          },
        }
      );
      setTeachers(response.data.data || []);
      setLoading(false); // Mark data as loaded
    } catch (error) {
      console.error("Error fetching teachers:", error);
      setError("Error fetching teachers");
      setLoading(false);
    }
  };

  // Fetch teachers on mount
  useEffect(() => {
    fetchTeachers();
  }, []);

  const [fieldGroups, setFieldGroups] = useState([
    {
      eventCategory: "",
      fields: [],
      currentField: "",
    },
  ]);

  const [formData, setFormData] = useState({
    allocated_teacher_id: "", // Track selected teacher
  });

  const handleAddFieldGroup = () => {
    setFieldGroups((prevGroups) => [
      ...prevGroups,
      { eventCategory: "", fields: [], currentField: "" },
    ]);
  };

  const handleRemoveFieldGroup = (index) => {
    setFieldGroups((prevGroups) => prevGroups.filter((_, i) => i !== index));
  };

  const handleCategoryChange = (index, value) => {
    const updatedFieldGroups = [...fieldGroups];
    updatedFieldGroups[index].eventCategory = value;
    setFieldGroups(updatedFieldGroups);
  };

  const handleFieldChange = (index, value) => {
    setFieldGroups((prevGroups) =>
      prevGroups.map((group, i) =>
        i === index ? { ...group, currentField: value } : group
      )
    );
  };

  const handleAddField = (index) => {
    setFieldGroups((prevGroups) =>
      prevGroups.map((group, i) =>
        i === index && group.currentField.trim()
          ? {
              ...group,
              fields: [...group.fields, group.currentField.trim()],
              currentField: "",
            }
          : group
      )
    );
  };

  const handleRemoveField = (groupIndex, fieldIndex) => {
    setFieldGroups((prevGroups) =>
      prevGroups.map((group, i) =>
        i === groupIndex
          ? {
              ...group,
              fields: group.fields.filter((_, fi) => fi !== fieldIndex),
            }
          : group
      )
    );
  };
  const handleTeacherChange = (index, value) => {
    setFieldGroups((prevGroups) =>
      prevGroups.map((group, i) =>
        i === index ? { ...group, allocated_teacher_id: value } : group
      )
    );
  };

  const handleSave = async () => {
    const schoolId = sessionStorage.getItem("school_id");
    const userId = sessionStorage.getItem("user_id");

    // Prepare the categories data
    const categoriesData = fieldGroups.map((group) => ({
      category_id: group.eventCategory,
      sub_categories: group.fields.map((field) => ({
        sub_category: field,
        allocated_teacher: group.allocated_teacher_id || "", // Get allocated teacher ID from the group
      })),
    }));

    const requestData = {
      user_id: userId,
      school_id: schoolId,
      event_id: eventId, // Use eventId here
      categories: categoriesData,
    };

    console.log("createee_sub_cat", requestData); // Debug the payload

    try {
      const response = await axios.post(
        `${BASE_URL}/admin/events/sub_category/create_subcat.php`,
        requestData
      );

      console.log("createee_sub_cat response", response);

      if (response.data.success) {
        toast({
          title: "Subcategories Created Successfully",
          description: "The subcategories have been created successfully.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onClose();
      } else {
        toast({
          title: "Error",
          description: `Error: ${response.data.message}`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error creating subcategories:", error);
      toast({
        title: "Error Creating Subcategories",
        description: "An error occurred while creating subcategories.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <>
      <Button
        leftIcon={<FaPlus />}
        colorScheme="teal"
        onClick={onOpen}
        display={{ base: "flex", md: "inline-flex" }} // Hide text on mobile (base breakpoint)
      >
        <Box display={{ base: "none", md: "inline" }}>Sub-Category</Box>
      </Button>

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            {" "}
            <Icon as={FaPlus} boxSize={5} mr={2} />
            Add Sub-Categories and Fields
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            {/* Event Dropdown (using event_id instead of event_title) */}
            <FormControl id="eventId" mb={4}>
              <FormLabel>
                <Icon as={FaCalendarAlt} mr={1} color="teal.500" />
                Event
              </FormLabel>
              <Select
                bg="white"
                _hover={{ borderColor: "teal.300" }}
                placeholder="Select Event"
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
              >
                {events.length > 0 ? (
                  events.map((event) => (
                    <option key={event.id} value={event.id}>
                      {event.event_title}
                    </option>
                  ))
                ) : (
                  <option>No events available</option>
                )}
              </Select>
            </FormControl>

            {fieldGroups.map((group, index) => (
              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                spacing={4}
                mb={6}
                key={index}
              >
                <VStack align="stretch" spacing={4}>
                  {/* Event Category Dropdown */}
                  <FormControl id={`eventCategory-${index}`}>
                    <FormLabel>
                      <Icon as={FaTags} mr={1} color="teal.500" />
                      Event Category
                    </FormLabel>
                    <Select
                      bg="white"
                      _hover={{ borderColor: "teal.300" }}
                      placeholder="Select Event Category"
                      value={group.eventCategory}
                      onChange={(e) =>
                        handleCategoryChange(index, e.target.value)
                      }
                    >
                      {filteredCategories.length > 0 ? (
                        filteredCategories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.category}
                          </option>
                        ))
                      ) : (
                        <option>No categories available</option>
                      )}
                    </Select>
                  </FormControl>

                  {/* Allocated Teachers Dropdown */}
                  <FormControl>
                    <FormLabel>
                      <Icon as={FaUserTie} mr={1} color="teal.500" />
                      Allocated Teacher
                    </FormLabel>
                    <Select
                      bg="white"
                      _hover={{ borderColor: "teal.300" }}
                      value={group.allocated_teacher_id || ""}
                      onChange={(e) =>
                        handleTeacherChange(index, e.target.value)
                      }
                    >
                      <option value="">Select Teacher</option>
                      {teachers.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.name}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </VStack>

                {/* Input Field and Dynamic List */}
                <VStack align="stretch" spacing={4}>
                  <FormControl id={`addField-${index}`}>
                    <FormLabel>
                      <Icon as={FaListUl} mr={1} color="teal.500" />
                      Add Sub-Category
                    </FormLabel>
                    <Box display="flex" gap={2}>
                      <Input
                        bg="white"
                        _hover={{ borderColor: "teal.300" }}
                        value={group.currentField}
                        onChange={(e) =>
                          handleFieldChange(index, e.target.value)
                        }
                        placeholder="Enter Sub-Category"
                      />
                      <Button
                        colorScheme="teal"
                        onClick={() => handleAddField(index)}
                      >
                        <FaPlus />
                      </Button>
                    </Box>
                  </FormControl>

                  <Box>
                    <FormLabel>
                      <Icon as={FaListUl} mr={1} color="teal.500" />
                      Selected Sub-Category
                    </FormLabel>
                    <VStack
                      bg="white"
                      _hover={{ borderColor: "teal.300" }}
                      align="stretch"
                      border="1px solid"
                      borderColor="gray.300"
                      borderRadius="md"
                      p={3}
                    >
                      {group.fields.length > 0 ? (
                        group.fields.map((field, fieldIndex) => (
                          <Box
                            key={fieldIndex}
                            p={2}
                            bg="gray.100"
                            borderRadius="md"
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <span>{field}</span>
                            <Button
                              size="sm"
                              colorScheme="red"
                              onClick={() =>
                                handleRemoveField(index, fieldIndex)
                              }
                            >
                              <FaTrash />
                            </Button>
                          </Box>
                        ))
                      ) : (
                        <Box>No fields added for this category.</Box>
                      )}
                    </VStack>
                  </Box>
                </VStack>
              </SimpleGrid>
            ))}

            {/* Add and Remove Category buttons */}
            <Button
              marginLeft={12}
              colorScheme="blue"
              variant="outline"
              onClick={handleAddFieldGroup}
            >
              Add Next Category
            </Button>
            <Button
              marginLeft={4}
              colorScheme="red"
              variant="outline"
              onClick={() => handleRemoveFieldGroup(fieldGroups.length - 1)}
              isDisabled={fieldGroups.length === 1}
            >
              Remove Last Category
            </Button>
          </ModalBody>

          <ModalFooter>
            <Button
              bgGradient="linear(to-r, teal.400, teal.600)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
              mr={3}
              onClick={handleSave}
            >
              Save
            </Button>
            {/* <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button> */}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Sub_Cat_form;
